import { Button } from "@/components/ui/button";
import {
  Check, ArrowRight, AppWindow, Headset, Users, MessageSquare, Mail, BarChart3,
  Workflow, Network, GraduationCap, Activity, Radar, Shield, Brain, MapPin,
  LineChart, UserCog,
} from "lucide-react";

const app = {
  tag: "VoxCampaign App",
  icon: AppWindow,
  title: "A plataforma para sua equipe operar campanhas com autonomia.",
  tone: "Sua equipe no controle.",
  features: [
    { i: Users, t: "Gestão completa de contatos" },
    { i: MessageSquare, t: "WhatsApp + E-mail integrados" },
    { i: Workflow, t: "Segmentação e automações" },
    { i: BarChart3, t: "Dashboards em tempo real" },
    { i: Network, t: "Múltiplas conexões" },
    { i: GraduationCap, t: "Treinamento incluso" },
    { i: UserCog, t: "Multiusuários por equipe" },
    { i: Mail, t: "Relatórios estratégicos" },
  ],
  ideal: ["Vereadores", "Pré-candidatos", "Campanhas menores", "Equipes internas"],
  href: "#planos",
  cta: "Conhecer Vox App",
};

const service = {
  tag: "VoxCampaign Service",
  icon: Headset,
  title: "Uma operação estratégica ao lado da sua campanha.",
  tone: "Nós operamos junto com você.",
  features: [
    { i: Headset, t: "Operação assistida" },
    { i: Users, t: "Squad estratégico dedicado" },
    { i: Activity, t: "Monitoramento contínuo" },
    { i: Brain, t: "IA aplicada à campanha" },
    { i: Shield, t: "Gestão anti-bloqueio" },
    { i: MapPin, t: "Inteligência territorial" },
    { i: LineChart, t: "Otimização contínua" },
    { i: Radar, t: "Acompanhamento humano" },
  ],
  ideal: ["Campanhas maiores", "Candidatos com equipe reduzida", "Alta demanda", "Operações complexas"],
  href: "#service",
  cta: "Conhecer Vox Service",
};

const Card = ({ data, dark }: { data: typeof app; dark?: boolean }) => {
  const Icon = data.icon;
  return (
    <div
      className={`relative rounded-3xl p-8 lg:p-10 flex flex-col overflow-hidden ${
        dark
          ? "bg-gradient-cta text-primary-foreground border border-white/10 shadow-elegant"
          : "bg-card border border-border shadow-card"
      }`}
    >
      {dark && (
        <>
          <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full bg-accent/40 blur-3xl pointer-events-none" />
          <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        </>
      )}
      <div className="relative">
        <div className="flex items-center gap-3">
          <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${
            dark ? "bg-white/15" : "bg-gradient-primary shadow-soft"
          }`}>
            <Icon className={`h-6 w-6 ${dark ? "text-highlight" : "text-primary-foreground"}`} />
          </div>
          <div className={`text-xs font-bold uppercase tracking-wider ${dark ? "text-highlight" : "text-accent"}`}>
            {data.tag}
          </div>
        </div>

        <h3 className="mt-5 text-2xl lg:text-3xl font-bold leading-tight">{data.title}</h3>
        <p className={`mt-3 text-sm font-semibold italic ${dark ? "text-highlight/90" : "text-accent"}`}>
          "{data.tone}"
        </p>

        <ul className="mt-7 grid sm:grid-cols-2 gap-x-4 gap-y-3 text-sm">
          {data.features.map(({ i: I, t }) => (
            <li key={t} className="flex items-start gap-2.5">
              <span className={`mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0 ${
                dark ? "bg-highlight/25" : "bg-success/15"
              }`}>
                <Check className={`h-3 w-3 ${dark ? "text-highlight" : "text-success"}`} strokeWidth={3} />
              </span>
              <span className={dark ? "text-primary-foreground/90" : "text-foreground"}>{t}</span>
            </li>
          ))}
        </ul>

        <div className={`mt-7 pt-6 border-t ${dark ? "border-white/15" : "border-border"}`}>
          <div className={`text-[11px] uppercase tracking-wider font-bold mb-2 ${
            dark ? "text-primary-foreground/70" : "text-muted-foreground"
          }`}>
            Ideal para
          </div>
          <div className="flex flex-wrap gap-2">
            {data.ideal.map((x) => (
              <span
                key={x}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                  dark
                    ? "bg-white/10 text-primary-foreground/90 border border-white/15"
                    : "bg-muted text-foreground border border-border"
                }`}
              >
                {x}
              </span>
            ))}
          </div>
        </div>

        <div className="mt-8">
          <Button
            asChild
            size="lg"
            className={`w-full h-12 text-base ${
              dark
                ? "bg-highlight text-highlight-foreground hover:opacity-95 shadow-glow"
                : "bg-foreground text-background hover:opacity-90"
            }`}
          >
            <a href={data.href}>
              {data.cta} <ArrowRight className="ml-1 h-4 w-4" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export function SolucoesSection() {
  return (
    <section id="solucoes" className="py-24 bg-background relative">
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider">
            Duas soluções. Uma infraestrutura.
          </div>
          <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-foreground">
            Escolha como sua campanha quer operar:{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">com autonomia ou com nosso time ao lado.</span>
          </h2>
          <p className="mt-5 text-lg text-muted-foreground">
            A VoxCampaign entrega tecnologia, operação e inteligência em dois formatos complementares — para cada nível de maturidade e complexidade política.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6 items-stretch">
          <Card data={app} />
          <Card data={service} dark />
        </div>
      </div>
    </section>
  );
}
